import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Receipt, Lock, User, Mail, ArrowRight,
  Loader2, ChevronLeft, ShieldAlert, KeyRound, RefreshCw,
  Eye, EyeOff
} from "lucide-react";

const API_BASE = "https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/bill-login";

export default function BillGeneratorLogin() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const [otp, setOtp] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* ================= TIMER ================= */
  useEffect(() => {
    if (step === 2 && timer > 0) {
      const interval = setInterval(() => {
        setTimer((t) => t - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step, timer]);

  /* ================= SEND OTP ================= */
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to send OTP");
      }

      setStep(2);
      setTimer(30);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= VERIFY OTP ================= */
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          otp
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "OTP verification failed");
      }

      navigate("/GenerateBillForm");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= RESEND OTP ================= */
  const handleResendOTP = async () => {
    setIsLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setTimer(30);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen w-full bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden font-sans text-slate-200">
      
      {/* Background Decor: Grid & Gradients */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-blue-500/10 blur-[120px]" />
      </div>

      <Link 
        to="/" 
        className="absolute top-6 left-6 z-20 flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/40 backdrop-blur-md border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-800 transition-all duration-300 group"
      >
        <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm font-medium">Back to Home</span>
      </Link>

      <div className="w-full max-w-[400px] z-10 relative">
        {/* Logo/Icon Area */}
        <div className="flex flex-col items-center mb-8 relative">
           {/* Glow Effect behind icon */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-emerald-500/30 blur-2xl rounded-full"></div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-4 rounded-2xl border border-slate-700 shadow-xl relative">
            <Receipt size={36} className="text-emerald-400" />
          </div>
        </div>

        {/* Glass Card */}
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
          
          {/* Top colored line for accent */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500"></div>

          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-white mb-2 tracking-tight">
              Bill Generator
            </h1>
            <p className="text-sm text-slate-400">
              {step === 1 ? "Sign in to access your operator dashboard" : "Enter the verification code sent to your email"}
            </p>
          </div>

          {error && (
            <div className="mb-6 flex items-start gap-3 text-sm text-red-200 bg-red-500/10 border border-red-500/20 rounded-xl p-3 animate-in fade-in slide-in-from-top-2">
              <ShieldAlert size={18} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleSendOTP} className="space-y-5">
              <div className="space-y-4">
                <Input icon={User} name="name" placeholder="Operator Name" value={formData.name} onChange={handleChange} />
                <Input icon={Mail} name="email" placeholder="Email Address" value={formData.email} onChange={handleChange} />
                <Input 
                  icon={Lock} 
                  type="password" 
                  name="password" 
                  placeholder="Password" 
                  value={formData.password} 
                  onChange={handleChange} 
                />
              </div>

              <button 
                disabled={isLoading} 
                className="group w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 hover:shadow-emerald-900/40 transition-all duration-300 transform active:scale-[0.98]"
              >
                {isLoading ? <Loader2 className="animate-spin" /> : <>Request OTP <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div className="bg-slate-950/30 rounded-xl p-4 border border-slate-800/50">
                <p className="text-xs text-slate-500 mb-2 text-center">One-Time Password</p>
                <Input
                  icon={KeyRound}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  className="text-center tracking-widest text-lg font-mono placeholder:font-sans placeholder:tracking-normal placeholder:text-sm"
                />
              </div>

              <button 
                disabled={isLoading} 
                className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium rounded-xl shadow-lg shadow-emerald-900/20 transition-all duration-300"
              >
                {isLoading ? <Loader2 className="animate-spin mx-auto" /> : "Verify & Continue"}
              </button>

              <div className="flex items-center justify-between text-xs font-medium pt-2">
                <button type="button" onClick={() => setStep(1)} className="text-slate-400 hover:text-white transition-colors">
                  Change Email
                </button>
                <button type="button" disabled={timer > 0} onClick={handleResendOTP}
                  className={`flex items-center gap-1.5 ${timer > 0 ? "text-slate-600 cursor-not-allowed" : "text-emerald-400 hover:text-emerald-300"}`}>
                  {timer > 0 ? <span>Resend in <span className="font-mono">{timer}s</span></span> : <><RefreshCw size={12} /> Resend OTP</>}
                </button>
              </div>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-slate-800/60 flex items-center justify-center gap-2 text-[11px] text-slate-500">
            <ShieldAlert size={12} className="text-emerald-500/80" />
            <span className="opacity-70">Secure encrypted connection</span>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ================= UPDATED INPUT COMPONENT ================= */
function Input({ icon: Icon, type = "text", className = "", ...props }) {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isPassword = type === "password";

  return (
    <div className={`relative group transition-all duration-300 ${isFocused ? 'scale-[1.01]' : ''}`}>
      <div className={`absolute left-3.5 top-3.5 transition-colors duration-300 ${isFocused ? 'text-emerald-400' : 'text-slate-500 group-hover:text-slate-400'}`}>
        <Icon size={18} />
      </div>
      
      <input
        {...props}
        type={isPassword ? (showPassword ? "text" : "password") : type}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        required
        className={`w-full bg-slate-950/40 border border-slate-800 rounded-xl py-3 pl-11 pr-10 text-sm text-white placeholder:text-slate-600 outline-none transition-all duration-300 
          focus:border-emerald-500/50 focus:bg-slate-900/80 focus:shadow-[0_0_15px_rgba(16,185,129,0.1)] 
          hover:border-slate-700 ${className}`}
      />

      {isPassword && (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3.5 top-3.5 text-slate-500 hover:text-white transition-colors cursor-pointer"
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      )}
    </div>
  );
}